import React, { useState, useEffect, useRef } from 'react';

const SVGPreview = ({ designId, className, fallbackSrc, alt, isPng }) => {
  const [svgContent, setSvgContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#f5f5f5');
  const containerRef = useRef(null);

  useEffect(() => {
    if (!designId) return;

    // If we know this is a PNG, skip SVG fetch
    if (isPng || (fallbackSrc && fallbackSrc.toLowerCase && fallbackSrc.toLowerCase().includes('.png'))) {
      setError(true);
      setIsLoading(false);
      return;
    }

    const fetchSVG = async () => {
      try {
        setIsLoading(true);
        setError(false);
        
        const response = await fetch(`/designs/${designId}/svg_content`);
        
        if (response.status === 422) {
          // This is a PNG design, use fallback
          setError(true);
          return;
        }
        
        const data = await response.json();
        
        if (data.success) {
          // Apply automatic background removal
          let processedSvg = data.svg_content;
          
          // Helper to detect background color
          const detectBackgroundColor = (svgText) => {
            // Look for the first rect element with a fill color
            const rectMatch = svgText.match(/<rect[^>]*fill="([^"]+)"[^>]*>/);
            if (rectMatch && rectMatch[1] && rectMatch[1] !== 'none' && rectMatch[1] !== 'transparent') {
              // Check if this rect is likely a background (has 100% width/height or covers viewBox)
              const rectTag = rectMatch[0];
              if (rectTag.includes('width="100%"') || rectTag.includes('height="100%"') || 
                  rectTag.includes('width="972"') || rectTag.includes('height="1152"')) {
                return rectMatch[1];
              }
            }
            
            // Look for large rectangles that likely represent backgrounds
            const viewBoxMatch = svgText.match(/viewBox="0\s+0\s+(\d+)\s+(\d+)"/);
            if (viewBoxMatch) {
              const viewBoxWidth = parseInt(viewBoxMatch[1]);
              const viewBoxHeight = parseInt(viewBoxMatch[2]);
              
              // Look for rectangles that cover at least 80% of the viewBox
              const largeRectRegex = /<rect[^>]*width="(\d+)"[^>]*height="(\d+)"[^>]*fill="([^"]+)"[^>]*>/g;
              let match;
              while ((match = largeRectRegex.exec(svgText)) !== null) {
                const width = parseInt(match[1]);
                const height = parseInt(match[2]);
                const fill = match[3];
                
                if (fill !== 'none' && fill !== 'transparent' &&
                    width >= viewBoxWidth * 0.8 && height >= viewBoxHeight * 0.8) {
                  return fill;
                }
              }
            }
            
            return null;
          };
          
          const bgColor = detectBackgroundColor(processedSvg);
          if (bgColor) {
            // Apply background removal for detected color
            const variants = [];
            if (bgColor.startsWith('#')) {
              variants.push(bgColor.toLowerCase());
              variants.push(bgColor.toUpperCase());
              variants.push(bgColor.substring(1).toLowerCase());
              variants.push(bgColor.substring(1).toUpperCase());
            } else {
              variants.push(bgColor.toLowerCase());
              variants.push(bgColor.toUpperCase());
              variants.push('#' + bgColor.toLowerCase());
              variants.push('#' + bgColor.toUpperCase());
            }
            
            variants.forEach(variant => {
              processedSvg = processedSvg.replace(
                new RegExp(`fill="${variant}"`, 'gi'),
                'fill="transparent"'
              );
              processedSvg = processedSvg.replace(
                new RegExp(`fill:${variant}`, 'gi'),
                'fill: transparent'
              );
            });
          }
          
          // Also apply pattern-based background removal
          // Method 1: Replace fill in Apparel_Color rect elements
          processedSvg = processedSvg.replace(
            /(<g\s+id="Apparel_Color"[^>]*>[\s\S]*?<rect[^>]+)fill="[^"]*"/gi,
            '$1fill="transparent"'
          );
          
          // Method 2: First rect with specific dimensions
          processedSvg = processedSvg.replace(
            /(<rect[^>]+width="972"[^>]+height="1152"[^>]+)fill="[^"]*"/gi,
            '$1fill="transparent"'
          );
          
          // Method 3: Background rect pattern
          processedSvg = processedSvg.replace(
            /<rect\s+x="-[\d.]+"[^>]+width="972"[^>]+fill="#[^"]+"/gi,
            match => match.replace(/fill="#[^"]+"/, 'fill="transparent"')
          );
          
          // Method 4: First large rect that covers most of the viewBox
          processedSvg = processedSvg.replace(
            /<rect\s+(?:x="0"\s+)?(?:y="0"\s+)?width="100%"\s+height="100%"[^>]*fill="[^"]+"/gi,
            match => match.replace(/fill="[^"]+"/, 'fill="transparent"')
          );
          
          // Method 5: More aggressive - remove fill from ANY rect that's positioned at origin
          processedSvg = processedSvg.replace(
            /<rect\s+(?:x="0"\s+)?(?:y="0"\s+)?[^>]*fill="[^"]+"/gi,
            match => {
              // Check if this rect is large enough to be a background
              const widthMatch = match.match(/width="(\d+%?)"/);
              const heightMatch = match.match(/height="(\d+%?)"/);
              if (widthMatch && heightMatch) {
                const width = widthMatch[1];
                const height = heightMatch[1];
                // If it's 100% or a large pixel value
                if ((width === '100%' && height === '100%') || 
                    (parseInt(width) > 500 && parseInt(height) > 500)) {
                  return match.replace(/fill="[^"]+"/, 'fill="transparent"');
                }
              }
              return match;
            }
          );
          
          // Method 6: Remove common background colors
          const commonBackgrounds = ['#ffffff', '#fff', '#f0f0f0', '#fafafa', '#f5f5f5', '#e0e0e0', '#eeeeee', '#eee'];
          commonBackgrounds.forEach(color => {
            processedSvg = processedSvg.replace(
              new RegExp(`fill="${color}"`, 'gi'),
              'fill="transparent"'
            );
            processedSvg = processedSvg.replace(
              new RegExp(`fill:${color}`, 'gi'),
              'fill:transparent'
            );
          });
          
          setSvgContent(processedSvg);
          // Extract dominant color after SVG is loaded
          setTimeout(() => extractDominantColor(processedSvg), 100);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error fetching SVG:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSVG();
  }, [designId, isPng, fallbackSrc]);

  const extractDominantColor = (svgString) => {
    try {
      // Look for background patterns similar to Amp's approach
      
      // 1. Background rectangle with fill (100% width/height)
      let match = svgString.match(/<rect[^>]*width=["']100%["'][^>]*height=["']100%["'][^>]*fill=["']([^"']+)["']/);
      if (match) {
        const color = normalizeColor(match[1]);
        if (color !== '#ffffff') {
          setBackgroundColor(lightenColor(color, 0.7));
          return;
        }
      }
      
      // 2. Style with background-color
      match = svgString.match(/background-color:\s*([^;}"']+)/);
      if (match) {
        const color = normalizeColor(match[1]);
        if (color !== '#ffffff') {
          setBackgroundColor(lightenColor(color, 0.7));
          return;
        }
      }
      
      // 3. First rectangle that might be background
      match = svgString.match(/<rect[^>]*fill=["']([^"']+)["']/);
      if (match) {
        const color = normalizeColor(match[1]);
        if (color !== '#ffffff') {
          setBackgroundColor(lightenColor(color, 0.7));
          return;
        }
      }
      
      // 4. Parse SVG and look for large rectangles
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
      const svg = svgDoc.querySelector('svg');
      
      if (svg) {
        const viewBox = svg.getAttribute('viewBox');
        let svgWidth, svgHeight;
        
        if (viewBox) {
          const [, , width, height] = viewBox.split(' ').map(Number);
          svgWidth = width;
          svgHeight = height;
        } else {
          svgWidth = parseFloat(svg.getAttribute('width') || 100);
          svgHeight = parseFloat(svg.getAttribute('height') || 100);
        }
        
        // Find the first large rectangle
        const rects = svg.querySelectorAll('rect');
        for (const rect of rects) {
          const width = parseFloat(rect.getAttribute('width') || 0);
          const height = parseFloat(rect.getAttribute('height') || 0);
          const fill = rect.getAttribute('fill');
          
          // If rectangle covers most of the SVG area
          if (width >= svgWidth * 0.8 && height >= svgHeight * 0.8 && fill) {
            const color = normalizeColor(fill);
            if (color !== '#ffffff') {
              setBackgroundColor(lightenColor(color, 0.7));
              return;
            }
          }
        }
      }
      
      // Default to light gray if no background found
      setBackgroundColor('#f5f5f5');
    } catch (err) {
      console.error('Error extracting dominant color:', err);
      setBackgroundColor('#f5f5f5');
    }
  };
  
  const normalizeColor = (color) => {
    if (!color || color === 'none' || color === 'transparent') return '#ffffff';
    
    // Handle hex colors
    if (color.match(/^#[0-9A-Fa-f]{6}$/)) {
      return color.toLowerCase();
    } else if (color.match(/^#[0-9A-Fa-f]{3}$/)) {
      // Convert 3-digit hex to 6-digit
      return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`.toLowerCase();
    } else if (color.match(/^[0-9A-Fa-f]{6}$/)) {
      return `#${color}`.toLowerCase();
    } else {
      // For named colors or other formats, default to white
      return '#ffffff';
    }
  };

  const lightenColor = (color, amount) => {
    // Convert hex to RGB
    let hex = color.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Lighten the color
    const newR = Math.round(r + (255 - r) * amount);
    const newG = Math.round(g + (255 - g) * amount);
    const newB = Math.round(b + (255 - b) * amount);
    
    // Convert back to hex
    const toHex = (val) => {
      const hex = val.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
  };

  if (isLoading) {
    return (
      <div className={className} style={{ 
        background: backgroundColor, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '200px',
        aspectRatio: '1 / 1'
      }}>
        <span>Loading...</span>
      </div>
    );
  }

  if (error && fallbackSrc) {
    // If there's an error and we have a fallback image, use it
    return <img src={fallbackSrc} alt={alt} className={className} />;
  }

  if (error || !svgContent) {
    return (
      <div className={className} style={{ 
        background: backgroundColor, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '200px',
        color: '#999',
        aspectRatio: '1 / 1'
      }}>
        <span>Unable to load design</span>
      </div>
    );
  }

  // Render the SVG content directly
  return (
    <div 
      ref={containerRef}
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent', // Always transparent background for SVGs with removed backgrounds
        aspectRatio: '1 / 1',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <div 
        dangerouslySetInnerHTML={{ __html: svgContent }}
        style={{
          width: '80%',
          height: '80%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />
    </div>
  );
};

export default SVGPreview;